import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as turf from '@turf/turf';
import './TestAreasVisualizer.scss';
import {
  parseUniqueTreatments,
  generateSummerColors,
} from '../../../utils/plotVisualization';
import { TestAreasVisualizerProps } from '../../../types/testAreasVisualizer.types';
import { createSVGPatterns } from '../../../utils/mapPatterns';
import { createVisualizationFeatures } from '../../../utils/visualizationBuilder';
import { getFeatureStyle } from '../../../utils/layerStyles';

const TestAreasVisualizer: React.FC<TestAreasVisualizerProps> = ({
  fieldBoundaryGeoJSON,
  trialBoundaryGeoJSON,
  ablineGeoJSON,
  plotWidthFt,
  plotLengthFt,
  bufferFt,
  plotBufferLengthFt = 0,
  plotBufferWidthFt = 0,
  isPlot = true,
  treatmentRates = '',
  nominalRate = 0,
  applicationUnits = '',
  applyTrialBufferToFieldBoundary = true,
  onValidationChange,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const layerRef = useRef<L.GeoJSON | null>(null);
  const onValidationChangeRef = useRef(onValidationChange);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update the ref when the callback changes
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  // Initialize the map
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      maxZoom: 20,
      minZoom: 10,
    });

    // Add satellite tile layer
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: 'Map tiles &copy; Esri',
        maxZoom: 22,
      },
    ).addTo(map);

    // Create SVG pattern definitions for hatching
    createSVGPatterns(map.getContainer());

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Create all the visualization features
  useEffect(() => {
    if (!mapInstanceRef.current || !fieldBoundaryGeoJSON) return;

    // Remove existing layer
    if (layerRef.current) {
      mapInstanceRef.current.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    setIsLoading(true);

    // Create visualization features using extracted utility
    const result = createVisualizationFeatures({
      fieldBoundaryGeoJSON,
      trialBoundaryGeoJSON,
      ablineGeoJSON,
      plotWidthFt,
      plotLengthFt,
      bufferFt,
      plotBufferLengthFt,
      plotBufferWidthFt,
      isPlot,
      treatmentRates,
      nominalRate,
      applyTrialBufferToFieldBoundary,
    });

    // Handle errors
    if (result.error) {
      setError(`Failed to create visualization: ${result.error}`);
      setIsLoading(false);
      return;
    }

    // Report validation results to parent
    if (onValidationChangeRef.current) {
      onValidationChangeRef.current(result.validation);
    }

    // Create and add the layer
    const geojsonLayer = L.geoJSON(turf.featureCollection(result.features), {
      style: getFeatureStyle,
    });

    geojsonLayer.addTo(mapInstanceRef.current);
    layerRef.current = geojsonLayer;

    // Fit map to field boundary
    mapInstanceRef.current.fitBounds(geojsonLayer.getBounds());

    setIsLoading(false);
    setError(null);
  }, [
    fieldBoundaryGeoJSON,
    trialBoundaryGeoJSON,
    ablineGeoJSON,
    plotWidthFt,
    plotLengthFt,
    bufferFt,
    plotBufferLengthFt,
    plotBufferWidthFt,
    isPlot,
    treatmentRates,
    nominalRate,
    applyTrialBufferToFieldBoundary,
  ]);

  return (
    <div>
      <div className="test-areas-visualizer">
        {error && <div className="test-areas-visualizer__error">{error}</div>}
        {isLoading && (
          <div className="test-areas-visualizer__loading">
            Loading visualization...
          </div>
        )}

        <div className="test-areas-visualizer__map" ref={mapContainerRef} />

        <div className="test-areas-visualizer__legend">
          <div className="test-areas-visualizer__legend-item">
            <div className="test-areas-visualizer__legend-color test-areas-visualizer__legend-color--field"></div>
            <span>Field Boundary</span>
          </div>
          <div className="test-areas-visualizer__legend-item">
            <div className="test-areas-visualizer__legend-color test-areas-visualizer__legend-color--field-buffer"></div>
            <span>Field Buffer ({bufferFt}ft)</span>
          </div>

          {/* Treatment Rate Legend */}
          {(() => {
            const uniqueTreatments = parseUniqueTreatments(
              treatmentRates,
              nominalRate,
            );
            const treatmentColors = generateSummerColors(
              uniqueTreatments.length,
            );

            return uniqueTreatments.map((rate: number, index: number) => (
              <div key={rate} className="test-areas-visualizer__legend-item">
                <div
                  className="test-areas-visualizer__legend-color"
                  style={{
                    backgroundColor: treatmentColors[index],
                    border: `1px solid ${treatmentColors[index]}`,
                  }}
                ></div>
                <span>
                  {rate === nominalRate ? 'Nominal: ' : ''}
                  {rate} {applicationUnits}
                </span>
              </div>
            ));
          })()}

          {isPlot && (plotBufferLengthFt > 0 || plotBufferWidthFt > 0) && (
            <div className="test-areas-visualizer__legend-item">
              <div className="test-areas-visualizer__legend-color test-areas-visualizer__legend-color--buffer"></div>
              <span>Plot Buffers</span>
            </div>
          )}
          {trialBoundaryGeoJSON && (
            <div className="test-areas-visualizer__legend-item">
              <div
                className="test-areas-visualizer__legend-color"
                style={{
                  backgroundColor: 'transparent',
                  border: '2px dashed #87CEEB',
                }}
              ></div>
              <span>Trial Boundary</span>
            </div>
          )}
          {ablineGeoJSON && (
            <div className="test-areas-visualizer__legend-item">
              <div
                className="test-areas-visualizer__legend-color"
                style={{
                  backgroundColor: '#dc3545',
                  border: '1px solid #dc3545',
                }}
              ></div>
              <span>AB Line</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestAreasVisualizer;
